import { Button, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { useState } from "react";
import { renderClose } from "../../common/dialog";
import { createEntityStore } from "../../common/entityStore";
import { propDateField, propEnumField, propTextField } from "../../common/entityStoreForm";
import { propLengthMax, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { useOfferMailOrderShipmentsWithBtoAmt } from "../../common/enum";
import { newSpecialAction, SpecialActionDto, SpecialActionEditDto } from "./types";
import { UseListStore } from "../../common/listStore";
import { formatDate } from "../../common/localization";
import { useDefaultValues } from "../../common/defaultValues";
import { dayjs } from "../../common/config";

export const SpecialActionDialog = (props: {
    close: () => void;
    specialAction: SpecialActionDto | null;
    onChange(): void;
    useListStore: UseListStore<SpecialActionDto>;
}) => {
    const landCode = useDefaultValues(p => p.clientData.landCode);
    const [useStore] = useState(() => {
        const items = props.useListStore.getState().items;
        const otherItems = items.filter(p => p.id !== props.specialAction?.id);
        const useStore = createEntityStore<SpecialActionEditDto>({
            data: props.specialAction == null ? newSpecialAction() : props.specialAction,
            path: "settings/specialActions",
            fetchOnInit: false,
            id: props.specialAction != null ? props.specialAction.id : null,
            onSaved() {
                props.onChange();
                props.close();
            },
            propValidators: [
                propRequired("name"),
                propLengthMax("name", 50),
                propLengthMax("description", 255),
                propValidValue("fromDate", "fromDateFormatted"), propRequired("fromDate"),
                propValidValue("toDate", "toDateFormatted"), propRequired("toDate"),
                propValueValidator("toDate", (p, e) => p != null && dayjs.tz(p).isBefore(dayjs.tz(e.fromDate)) && "'Valid to' cannot be before 'Valid from'."),
                propValueValidator("toDate", (p, e) => {
                    if (e.fromDate == null || e.toDate == null) {
                        return null;
                    }
                    const overlaps = otherItems.filter(p => dayjs.tz(p.fromDate) <= dayjs.tz(e.toDate) && dayjs.tz(p.toDate) >= dayjs.tz(e.fromDate));
                    return overlaps.length === 0 ? null : `Range overlaps with other special actions (${overlaps.map(p =>
                        `${formatDate(p.fromDate, landCode)} - ${formatDate(p.toDate, landCode)}`).join(", ")})`;
                }),
            ]
        });
        return useStore;
    });
    const { save, isSaveDisabled, serverData } = useStore(p => ({ save: p.save, isSaveDisabled: p.isSaveDisabled, serverData: p.serverData }));
    return <>
        <DialogTitle>{serverData.id == null ? `New special action` : `Edit special action - ${serverData.name}`}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2} alignItems="stretch">
                <Grid item xs={12}>{propTextField(useStore)("name", "Name")}</Grid>
                <Grid item xs={12}>{propTextField(useStore)("description", "Description")}</Grid>
                <Grid item xs={6}>{propDateField(useStore)("fromDate", "fromDateFormatted", "Valid from")}</Grid>
                <Grid item xs={6}>{propDateField(useStore)("toDate", "toDateFormatted", "Valid to")}</Grid>
                <Grid item xs={12}>{propEnumField(useStore)("template1OfferMailOrderShipmentId", "MO 1", useOfferMailOrderShipmentsWithBtoAmt)}</Grid>
                <Grid item xs={12}>{propEnumField(useStore)("template2OfferMailOrderShipmentId", "MO 2", useOfferMailOrderShipmentsWithBtoAmt)}</Grid>
                <Grid item xs={12}>{propEnumField(useStore)("template3OfferMailOrderShipmentId", "MO 3", useOfferMailOrderShipmentsWithBtoAmt)}</Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={save} disabled={isSaveDisabled} color="save">Save</Button>
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
};